import { breathStabilityScore } from '@muzly/voice-engine';
import type { MetricKey } from '@muzly/voice-engine';

import { conditionScore } from '../onboarding/fatigueRadar';
import { brightnessPosition, stepOfPosition } from '../onboarding/resultCopy';
import { visibleTabs } from '../onboarding/visibleTabs';

import type { CopyRef } from '../../core';
import type { DiagnosisRecord } from '../onboarding/record';
import type { MetricTabId } from '../onboarding/resultCopy';

/**
 * 마지막 회차 → 홈의 지표 타일(시안 §7.1)과 이력 목록의 지표 줄. 순수 변환이다.
 *
 * 타일은 결과 상세의 **탭과 같은 일곱**이고(`METRIC_TAB_IDS`가 그 목록의 주인이다),
 * 누르면 그 탭으로 간다(`/result/:id/:tab`).
 *
 * **순서는 상세와 다르다.** 시안 §7.1은 `음정`을 `음역`보다 앞에 두고 상세 §8.2는 반대다.
 * 목록이 같고 순서만 다른 것이라 여기 순서를 상세에 맞추면 홈이 시안과 어긋난다.
 *
 * **못 잰 값은 `null`이다** — 0도 `00`도 아니다. 시안이 그려 둔 `00`은 자리표시자지 값이
 * 아니라, 그대로 옮기면 화면이 재지도 않은 0점을 말한다.
 *
 * 그 `null` 줄을 **그릴지 말지는 화면마다 다르다**: 홈은 걸러내고(`homeTiles`) 이력은
 * 남긴다(`metricTiles`). 각 함수 주석이 그 이유를 갖는다.
 *
 * React·저장소 IO 의존 없음 — vitest 대상.
 */

export interface HomeTile {
  /** 누르면 갈 상세 탭. 타일 목록과 탭 목록이 같은 이름을 쓴다. */
  tab: MetricTabId;
  /**
   * 화면에 낼 값. 서식 있는 값은 `valueKey` 쪽에 실린다.
   *
   * **서식이 붙은 값은 여기 없다** — 단위와 구분자는 로케일이 갖는다(`valueKey`).
   * 코드가 `${low}-${high}`를 만들면 en 로케일이 그 자리를 못 건드린다.
   */
  value: string | null;
  /**
   * 서식이나 문장이 필요한 값의 i18n 키. 렌더에서 번역한다 — 모듈 초기화 때 번역해 두면
   * 언어를 바꿔도 이 줄만 안 바뀐다.
   */
  valueKey?: string;
  /** `valueKey`에 넣을 값들. */
  valueParams?: Record<string, string | number>;
}

/**
 * 밝기 타일의 i18n 키 — **상세 화면과 같은 문장**을 쓴다. 같은 값을 홈이 `밝은 편`,
 * 상세가 다른 말로 부르면 두 지표로 읽힌다.
 */
export const brightnessLabelKey = (brightnessHz: number): string =>
  `web.result.resonance.brightnessLevel.${stepOfPosition(
    brightnessPosition(brightnessHz),
  )}`;

/**
 * 회차 → 지표 일곱 줄(`METRIC_TAB_IDS` 전부, 못 잰 것도 포함). **이력 목록이 쓰는 쪽**이다
 * (이력 카드는 회차 값이 아닌 피로도 줄을 뺀다):
 * 이력은 회차끼리 견주는 화면이라 줄이 회차마다 들락거리면 카드 둘을 나란히 못 읽는다.
 * 빈 줄은 사라지는 대신 값 칸만 빈 상태 문구다(플랜 § Design 빈 자리 정책).
 *
 * @param record 그릴 회차.
 * @param history 저장된 회차 전부. 피로도 타일만 쓴다 — 안 주면 그 타일은 값이 없다.
 */
export const metricTiles = (
  record: DiagnosisRecord,
  history: readonly DiagnosisRecord[] = [],
): HomeTile[] => {
  // 키를 `MetricKey`로 좁힌다 — `string`이면 오타가 컴파일을 통과해 그 타일이 조용히
  // 미측정으로 뜬다. 값이 없다는 판정을 오타가 대신하게 두지 않는다.
  const scoreOf = (key: MetricKey): string | null => {
    const found = record.scores.find((entry) => entry.key === key);
    return found ? String(Math.round(found.val)) : null;
  };
  const breath = breathStabilityScore(record.breath ?? []);

  // 피로도는 이 회차가 답한 컨디션의 축 값이다 — 탭 머리가 **같은 함수**를 불러 두
  // 화면이 같은 숫자를 말한다.
  //
  // **탭이 서는지부터 같은 술어에 묻는다**(`visibleTabs`). 값의 유무만 보면 축 하나짜리
  // 이력에서 타일은 숫자를 내는데 탭은 안 서서, 타일을 누른 사람이 다른 탭으로 튕긴다 —
  // 술어를 둘로 나누면 반드시 갈라지는 자리다(plan § Boundaries).
  const fatigue = visibleTabs(record, history).includes('fatigue')
    ? conditionScore(record, history)
    : null;

  return [
    { tab: 'fatigue', value: fatigue == null ? null : String(fatigue) },
    { tab: 'registers', value: scoreOf('pitchAccuracy') },
    {
      // 서식은 로케일이 갖는다. 상세(`web.result.range.notes`)는 `E2 ~ C5`로 넓게 쓰고
      // 홈 타일은 좁아서 `E2-C5`로 붙인다 — 시안 §7.1과 §8.4가 실제로 그렇게 다르다.
      tab: 'range',
      value: null,
      valueKey: record.range ? 'web.home.tiles.range' : undefined,
      valueParams: record.range
        ? { low: record.range.lowNote, high: record.range.highNote }
        : undefined,
    },
    // 떨림 타일은 **속도**를 낸다(시안 §7.1). 탭 머리의 `vibrato` 점수는 속도·폭을 참고
    // 구간에 댄 판정이라 타일 칸에 넣으면 단위 없는 숫자가 된다.
    {
      tab: 'vibrato',
      value: null,
      valueKey: record.vibrato ? 'web.home.tiles.hz' : undefined,
      valueParams: record.vibrato
        ? { value: record.vibrato.rateHz.toFixed(1) }
        : undefined,
    },
    // 호흡 안정도 탭 머리와 같은 값 — 구간 점수의 평균(`breathStabilityScore`).
    { tab: 'breath', value: breath == null ? null : String(breath) },
    // 싱어즈 포먼트는 점수가 아니라 원값(중심 주파수)이 저장된다.
    {
      tab: 'formant',
      value: null,
      valueKey:
        record.resonanceCenterHz == null ? undefined : 'web.home.tiles.hz',
      valueParams:
        record.resonanceCenterHz == null
          ? undefined
          : { value: Math.round(record.resonanceCenterHz) },
    },
    {
      tab: 'resonance',
      value: null,
      valueKey:
        record.brightnessHz == null
          ? undefined
          : brightnessLabelKey(record.brightnessHz),
    },
  ];
};

/**
 * 홈 타일 — **잰 지표만**. 홈은 회차 하나를 한눈에 보는 화면이라 빈 타일이 설 이유가 없다:
 * 눌러도 빈 탭으로 갈 뿐이었고(2026-09-16 실기기), `측정 준비 중`은 App Store 심사가 보는
 * 미완 표식이기도 하다. 이력이 같은 규칙을 쓰지 않는 이유는 `metricTiles` 주석에 있다.
 *
 * @param record 마지막 회차. 없으면 호출부가 타일을 안 그린다.
 * @param history 저장된 회차 전부 — 피로도 타일만 쓴다.
 */
export const homeTiles = (
  record: DiagnosisRecord,
  history: readonly DiagnosisRecord[] = [],
): HomeTile[] => {
  // 잰 지표인지는 탭과 **같은 술어**에 묻는다(`visibleTabs`). 타일 값의 유무로 거꾸로
  // 추론하면 두 판정이 따로 유지돼, 새 지표 하나에 홈과 탭이 갈라진다.
  //
  // 값도 따로 본다 — 피로도 탭은 이력이 세우지만 타일 값은 **이 회차의** 컨디션 축이라,
  // 컨디션을 안 답한 마지막 회차면 탭은 서는데 타일 칸은 빈다.
  const shown = visibleTabs(record, history);
  return metricTiles(record, history).filter(
    (tile) =>
      shown.includes(tile.tab) &&
      (tile.value !== null || tile.valueKey !== undefined),
  );
};

/**
 * 타일 n개를 12열 그리드에 어떻게 나눠 앉힐까 — **한 줄에 넷까지, 줄마다 고르게**
 * 나눈 뒤 각 줄이 12를 꽉 채운다.
 *
 * 타일 수가 회차마다 달라지기 때문에 필요하다: 시안 §7.1의 4+3 배치를 `i < 4 ? 3 : 4`로
 * 굳혀 두면 여섯 개짜리 회차의 마지막 줄에 타일 하나 폭의 구멍이 남는다. 줄 수를 먼저
 * 정하고 고르게 나누는 이유도 같다 — 넷씩 채우고 남기면 다섯 개짜리의 마지막 줄에
 * 화면 폭을 다 쓰는 타일 하나가 선다.
 *
 * 일곱 개일 때 나오는 값은 시안과 같다(넷 + 셋 = `[3,3,3,3,4,4,4]`).
 */
export const tileSpans = (count: number): number[] => {
  const rows = Math.ceil(count / 4);
  const spans: number[] = [];
  let left = count;
  for (let row = rows; row > 0; row -= 1) {
    const size = Math.ceil(left / row);
    for (let i = 0; i < size; i += 1) {
      spans.push(12 / size);
    }
    left -= size;
  }
  return spans;
};

const two = (n: number): string => String(n).padStart(2, '0');

/**
 * 타일 헤더의 회차 날짜(시안 `2026. 00. 00`) — 로컬 날짜 필드를 로케일 서식에 끼운다
 * (`history/historyRows.ts`의 `formatDate`와 같은 방식, 연도까지 있는 변형).
 */
export const tileDate = (epochMs: number): CopyRef => {
  const d = new Date(epochMs);
  return {
    key: 'web.home.tiles.date',
    params: {
      year: d.getFullYear(),
      month: two(d.getMonth() + 1),
      day: two(d.getDate()),
    },
  };
};
